import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import ReactPlaceHolder from 'react-placeholder';
import { connect, useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import PageHeader from 'commons/src/components/headers/PageHeader';
import SubHeader from 'commons/src/components/headers/SubHeader';
import { mediumFormLoader } from 'commons/src/components/placeholder';
import { RequiredRoleLevel } from 'commons/src/models/commonEnums';
import { ActionButton } from 'commons/src/models/menuModels';
import { fetchAlertEvents, fetchThirdPartyIntegrations } from '../../../actions/thirdPartyIntegrationActions';
import { alertRuleThresholdCategories, paths, alertSystemNotificationRules } from '../../../constants';
import { NotificationAlertEvent, ThirdPartyIntegrationAlert } from '../../../models/common';
import { Store } from '../../../reducers';
import AlertHistoryInfo from './AlertEventInfo';

type Props = {
    alertEvents: NotificationAlertEvent[];
    notificationAlerts: ThirdPartyIntegrationAlert[];
    loadingThirdPartyIntegrations: boolean;
    loadingAlertEvents: boolean;
    loadingLocations: boolean;
};

export const AlertHistoryComponent = ({
    alertEvents,
    loadingThirdPartyIntegrations,
    loadingAlertEvents,
    loadingLocations,
    notificationAlerts,
}: Props): React.ReactElement => {
    const { historyId: alertHistoryId } = useParams<'historyId'>() as { historyId: string };
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const eventExists = alertEvents.find(alert => alert.id === alertHistoryId);
    const { t: txt } = useTranslation();

    useEffect(() => {
        if (!loadingAlertEvents && !loadingLocations && !eventExists) {
            navigate(`/${paths.history}`);
        }
    }, [loadingAlertEvents, loadingLocations, eventExists]);

    useEffect((): void => {
        if (alertEvents.length === 0 && !loadingAlertEvents) {
            dispatch(fetchAlertEvents());
        }
        if (notificationAlerts.length === 0 && !loadingThirdPartyIntegrations) {
            dispatch(fetchThirdPartyIntegrations());
        }
    }, []);

    let pageTitle = '';
    if (eventExists && eventExists.content.type === alertSystemNotificationRules.deviceOffline) {
        pageTitle = txt('NotificationAlerts.OfflineDeviceEventTitle');
    } else if (eventExists) {
        pageTitle = txt(eventExists.content.type).concat(
            eventExists.content.thresholdType === alertRuleThresholdCategories.allAbove
                ? txt('NotificationAlerts.IsHigh')
                : txt('NotificationAlerts.IsLow')
        );
    }

    const pageLoading = loadingAlertEvents || loadingThirdPartyIntegrations || loadingLocations;
    const actionButtons: ActionButton[] = [
        {
            onClick: (): void => navigate(`/${paths.history}`),
            testAttr: 'close-alert-element',
            title: 'Close',
            id: 'closeAlertElement',
            color: 'secondary',
            requiredRoleLevel: RequiredRoleLevel.ANY_ROLE,
            requiredGroupTypes: [],
        },
    ];
    return (
        <>
            <PageHeader title={pageTitle} />
            <div className="page-wrapper__medium">
                <SubHeader actionButtons={actionButtons} />
            </div>
            <ReactPlaceHolder ready={!pageLoading} customPlaceholder={mediumFormLoader}>
                <div>{eventExists && <AlertHistoryInfo alertEvent={eventExists} />}</div>
            </ReactPlaceHolder>
        </>
    );
};

const mapStateToProps = (state: Store): Props => {
    const {
        thirdPartyIntegrations: { alertEvents, notificationAlerts },
        requests: {
            FETCH_THIRD_PARTY_INTEGRATIONS: { loading: loadingThirdPartyIntegrations },
            FETCH_ALERT_EVENTS: { loading: loadingAlertEvents },
        },
        locations: { loading: loadingLocations },
    } = state;

    return {
        alertEvents,
        loadingLocations,
        loadingAlertEvents,
        loadingThirdPartyIntegrations,
        notificationAlerts,
    };
};

export default connect(mapStateToProps)(AlertHistoryComponent);
